import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  IconButton,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { X } from 'lucide-react';
import dayjs from 'dayjs';
import { CalendarEventType } from './types';

interface CalendarEventDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (event: Omit<CalendarEventType, 'id'>) => void;
  selectedDate?: dayjs.Dayjs;
}

const eventTypes = [
  { value: 'meeting', label: 'Meeting' },
  { value: 'deadline', label: 'Deadline' },
  { value: 'hearing', label: 'Hearing' },
  { value: 'deposition', label: 'Deposition' },
  { value: 'reminder', label: 'Reminder' },
  { value: 'other', label: 'Other' },
];

export const CalendarEventDialog: React.FC<CalendarEventDialogProps> = ({
  open,
  onClose,
  onSave,
  selectedDate,
}) => {
  const [formData, setFormData] = useState({
    title: '',
    date: dayjs(),
    type: 'meeting' as string,
    priority: 'medium' as const,
  });

  // Update form when dialog opens with selected date
  useEffect(() => {
    if (open && selectedDate) {
      setFormData(prev => ({
        ...prev,
        date: selectedDate,
      }));
    }
  }, [open, selectedDate]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      title: formData.title,
      type: formData.type,
      priority: formData.priority,
      date: formData.date.toISOString(),
    });
    setFormData({
      title: '',
      date: dayjs(),
      type: 'meeting',
      priority: 'medium',
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          Add Calendar Event
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{ color: 'text.secondary' }}
          >
            <X size={20} />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <FormControl fullWidth>
              <InputLabel>Event Type</InputLabel>
              <Select
                value={formData.type}
                label="Event Type"
                onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
                required
              >
                {eventTypes.map(type => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Event Title"
              fullWidth
              required
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
            />

            <DatePicker
              label="Event Date"
              value={formData.date}
              onChange={(date) => setFormData(prev => ({ ...prev, date: date || dayjs() }))}
              slotProps={{
                textField: {
                  fullWidth: true,
                  required: true,
                }
              }}
            />

            <FormControl fullWidth>
              <InputLabel>Priority</InputLabel>
              <Select
                value={formData.priority}
                label="Priority"
                onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value as any }))}
              >
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="high">High</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose} color="inherit">
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
          >
            Add Event
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};